/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Card,
  Spin,
  Descriptions,
  Modal,
  Row,
  Tag,
} from 'antd';
import {
  getCategoriasDeQuejas,
  getMotivosDeQuejas,
  getQueja,
} from 'api/solicitudes/quejas';
import { useLocation } from 'react-router';
import { Text, Title } from 'components';
import { getPlantillasNoIndexadas } from 'api/tramites';
import { getDependencias } from 'api/configuracion';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCategoriasDeQuejas,
  selectMotivosDeQuejas,
  selectSubMotivosDeQuejas,
  selectTiposDeVisitasDomiciliarias,
} from 'store/catalogos';

const ConsultaQueja = ({ queja: folioQueja }) => {
  const motivos = useSelector(selectMotivosDeQuejas);
  const subMotivos = useSelector(selectSubMotivosDeQuejas);
  const categorias = useSelector(selectCategoriasDeQuejas);
  const tiposDeVisitasDomiciliarias = useSelector(selectTiposDeVisitasDomiciliarias);
  const dispatch = useDispatch();
  const [queja, setQueja] = useState({});
  const [loading, setLoading] = useState(true);
  const [oficinaDeAtencion, setOficinaDeAtencion] = useState({});
  const [plantilla, setPlantilla] = useState({});
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const _folioQueja = folioQueja || location.state?.queja;

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      const [_queja] = await Promise.all([
        getQueja({
          folio: _folioQueja,
          referencia_de_seguridad: location.state?.codigo,
        }),
        getMotivosDeQuejas(dispatch),
        getCategoriasDeQuejas(dispatch),
      ]);
      if (mounted) {
        setQueja(_queja[0]);
        setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  const getOficinaDeAtencion = async (id) => {
    const { results: [oficina] } = await getDependencias({ id });
    setOficinaDeAtencion(oficina);
  };

  const getPlantillaDeTramites = async (id) => {
    const { results: [_plantilla] } = await getPlantillasNoIndexadas({ id });
    setPlantilla(_plantilla);
  };

  useEffect(() => {
    if (!queja) return;
    if (queja?.unidades_operativas) {
      getOficinaDeAtencion(queja?.unidades_operativas);
    }
    if (queja?.plantilla_de_tramites) {
      getPlantillaDeTramites(queja?.plantilla_de_tramites);
    }
    setIsAnonymous(!queja?.ciudadano && !queja?.contribuyentes);
    setVisible(location.state?.showModal);
  }, [queja]);

  return (
    <Spin spinning={loading} tip="Cargando...">
      <Card
        title={(
          <Title level={2}>
            Consulta del folio:
            {' '}
            {queja.folio}
          </Title>
        )}
      >
        <Descriptions
          bordered={!isAnonymous}
          size="small"
          title={(
            <Title level={3}>
              {isAnonymous ? 'Solicitante anónimo' : 'Datos del solicitante'}
            </Title>
          )}
          layout="vertical"
        >
          {!isAnonymous && (
            <>
              <Descriptions.Item
                label={(<Text>Nombre</Text>)}
              >
                {queja?.nombre_de_solicitante_de_queja}
              </Descriptions.Item>
              <Descriptions.Item
                label={(<Text>Apellido Paterno</Text>)}
              >
                {queja?.apellido_paterno}
              </Descriptions.Item>
              <Descriptions.Item
                label={(<Text>Apellido Materno</Text>)}
              >
                {queja?.apellido_materno}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
        <br />
        <Descriptions
          bordered
          size="small"
          title={(
            <Title level={3}>
              Datos de la solicitud
            </Title>
          )}
          layout="vertical"
          column={{
            xl: 3,
            lg: 2,
            md: 2,
            sm: 2,
            xs: 1,
          }}
        >
          {queja?.tipo_de_visita_domiciliaria ? (
            <Descriptions.Item
              label={(<Text>¿Trámite o visita?</Text>)}
              span={1}
            >
              {queja?.es_tramite ? 'Trámite' : 'Visita'}
            </Descriptions.Item>
          ) : (
            <Descriptions.Item
              label={(<Text>¿Qué se está reportanto?</Text>)}
              span={1}
            >
              {categorias.find((categoria) => categoria.id === queja?.categoria_de_la_queja)
                ?.descripcion}
            </Descriptions.Item>
          )}
          {queja?.tipo_de_visita_domiciliaria && (
            <Descriptions.Item
              label={(<Text>Tipo de protesta</Text>)}
              span={1}
            >
              {tiposDeVisitasDomiciliarias
                .find((t) => t.id === queja?.tipo_de_visita_domiciliaria).nombre}
            </Descriptions.Item>
          )}
          {motivos.find((motivo) => motivo.id === queja?.motivo_de_la_queja) && (
            <Descriptions.Item
              label={(<Text>Tipo de protesta</Text>)}
              span={1}
            >
              {motivos.find((motivo) => motivo.id === queja?.motivo_de_la_queja).descripcion}
            </Descriptions.Item>
          )}
          {subMotivos.find((motivo) => motivo.id === queja?.submotivo_de_la_queja) && (
            <Descriptions.Item
              label={(<Text>Motivo de la queja</Text>)}
              span={1}
            >
              {subMotivos
                .find((motivo) => motivo.id === queja?.submotivo_de_la_queja).descripcion}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={(<Text>Fecha</Text>)}
            span={1}
          >
            {queja?.fecha_de_los_hechos?.format('DD/MM/YYYY')}
          </Descriptions.Item>
          <Descriptions.Item
            label={(<Text>Hora</Text>)}
            span={1}
          >
            {queja?.hora_de_los_hechos?.toString()}
          </Descriptions.Item>
          <Descriptions.Item
            label={(<Text>Tramite/Servicio que quería realizar</Text>)}
            span={1}
          >
            {plantilla?.id ? plantilla.nombre : queja?.oficina_de_queja}
          </Descriptions.Item>
          <Descriptions.Item
            label={(
              <Text>
                De que institución es el Trámite/Servicio o el personal con quien trató
              </Text>
            )}
            span={1}
          >
            {oficinaDeAtencion?.id ? oficinaDeAtencion.descripcion : queja?.institucion_de_queja}
          </Descriptions.Item>
          <Descriptions.Item
            label={(<Text>¿Hay Pruebas?</Text>)}
            span={1}
          >
            {queja?.tiene_pruebas ? 'Sí' : 'No'}
          </Descriptions.Item>
          {!!queja?.testigos_de_quejas?.length && (
            <Descriptions.Item
              label={(<Text>¿Hubo testigos?</Text>)}
              span={1}
            >
              Sí
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={(<Text>Observaciones de resolución</Text>)}
            span={4}
          >
            {queja?.observaciones_queja || 'Sin observaciones aún...'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Modal
        visible={visible}
        centered
        title={(
          <Title level={2} $accent>
            Codigo de seguridad la
            {' '}
            {queja?.tipo_de_visita_domiciliaria ? 'visita' : 'queja'}
          </Title>
        )}
        onCancel={() => setVisible(false)}
        footer={null}
        maskClosable={false}
      >
        <Text>
          Este es el código de seguridad y folio de la queja, por favor no lo comparta con nadie.
        </Text>
        <br />
        <Text>
          Si desea volver a consultar la queja, por favor proporcione este código y folio.
        </Text>
        <br />
        <br />
        <Row justify="center">
          <Title level={3}>
            Folio
          </Title>
        </Row>
        <Row justify="center">
          <Tag style={{ padding: 10, borderRadius: 5 }}>
            <Title level={2}>
              {queja?.folio}
            </Title>
          </Tag>
        </Row>
        <br />
        <Row justify="center">
          <Title level={3}>
            Código de seguridad
          </Title>
        </Row>
        <Row justify="center">
          <Tag style={{ padding: 10, borderRadius: 5 }}>
            <Title level={2}>
              {queja?.referencia_de_seguridad}
            </Title>
          </Tag>
        </Row>
        <br />
        <br />
        <span>
          Si por error cierra esta ventana, puede volver a consultar la queja con el código de
          seguridad y folio.
        </span>
      </Modal>
    </Spin>
  );
};

export default ConsultaQueja;
